import { useMemo, useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import {
  ArrowRight,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  FileText,
  Mic,
  Pencil,
  Plus,
  Save,
  Sparkles,
  Trash2,
  Video,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { DragHandle, SortableList } from "@/components/SortableList";
import {
  useElementsForStandard,
  useInstrumentsForStandard,
  useStandard,
} from "@/store/hooks";
import { useDemoStore } from "@/store/useDemoStore";
import { suggestElements } from "@/lib/ai/simulate";
import type { Criterio, Element as StandardElement, Evidencia } from "@/data";

type CriterioTipo = Criterio["tipo"];
type EvidenciaTipo = Evidencia["tipo"];

const tipoOrder: CriterioTipo[] = ["desempeño", "producto", "conocimiento", "actitud", "emergencia"];
const tipoLabel: Record<CriterioTipo, string> = {
  desempeño: "Criterios de desempeño",
  producto: "Criterios de producto",
  conocimiento: "Criterios de conocimiento",
  actitud: "Criterios de actitud",
  emergencia: "Respuestas ante situaciones emergentes",
};
const tipoTone: Record<CriterioTipo, "teal" | "navy" | "amber" | "green"> = {
  desempeño: "teal",
  producto: "navy",
  conocimiento: "amber",
  actitud: "green",
  emergencia: "amber",
};

const evidenceIcons: Record<EvidenciaTipo, React.ComponentType<{ size?: number; color?: string }>> = {
  video: Video,
  foto: Camera,
  documento: FileText,
  audio: Mic,
};

function uid(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-3)}`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function CriteriaConfig() {
  const params = useParams<{ codigo: string; elementId: string }>();
  const codigo = params.codigo ?? "";
  const elementId = params.elementId ?? "";
  const [, setLocation] = useLocation();

  const standard = useStandard(codigo);
  const elems = useElementsForStandard(codigo);
  const instruments = useInstrumentsForStandard(codigo);
  const element = elems.find((e) => e.id === elementId);

  const upsertElement = useDemoStore((s) => s.upsertElement);
  const removeElement = useDemoStore((s) => s.removeElement);
  const updateStandard = useDemoStore((s) => s.updateStandard);
  const logEvent = useDemoStore((s) => s.logEvent);
  const activeRole = useDemoStore((s) => s.activeRole);

  const [editingHeader, setEditingHeader] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({ titulo: "", descripcion: "" });
  const [openSection, setOpenSection] = useState<Record<string, boolean>>({
    desempeño: true,
    producto: true,
    conocimiento: true,
    actitud: true,
    evidencias: true,
  });
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [criterioDraft, setCriterioDraft] = useState<Partial<Criterio>>({});
  const [evidenciaDraft, setEvidenciaDraft] = useState<Partial<Evidencia>>({});
  const [adding, setAdding] = useState<CriterioTipo | "evidencia" | null>(null);
  const [newCriterio, setNewCriterio] = useState<Partial<Criterio>>({});
  const [newEvidencia, setNewEvidencia] = useState<Partial<Evidencia>>({});
  const [aiPreview, setAiPreview] = useState<Criterio[] | null>(null);
  const [aiSel, setAiSel] = useState<Record<string, boolean>>({});

  // Criterio → instrumentos que lo cubren (por criterioIds del instrumento)
  const coverage = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const ins of instruments) {
      const ids = ins.criterioIds ?? [];
      for (const cid of ids) {
        const arr = map.get(cid) ?? [];
        arr.push(ins.nombre);
        map.set(cid, arr);
      }
    }
    return map;
  }, [instruments]);

  if (!standard || !element) {
    return (
      <AppLayout breadcrumb={["Estándares", codigo, "Elemento"]}>
        <Card>
          <SectionTitle>Elemento no encontrado</SectionTitle>
          <div style={{ fontSize: 13, color: "var(--cc-gray-600)", marginBottom: 12 }}>
            El elemento solicitado ya no existe en el catálogo.
          </div>
          <Link href={`/admin/estandares/${codigo}`} data-testid="link-back-standard">
            <PrimaryButton variant="outline">Volver al estándar</PrimaryButton>
          </Link>
        </Card>
      </AppLayout>
    );
  }

  function persistElement(next: StandardElement, accion: string) {
    upsertElement(codigo, next);
    // Sync conteos en el estándar
    const others = elems.filter((e) => e.id !== next.id);
    const allCriterios = [...others, next].reduce((a, e) => a + e.criterios.length, 0);
    updateStandard(codigo, {
      elementos: others.length + 1,
      criterios: allCriterios,
      ultimaActualizacion: today(),
    });
    logEvent({
      actor: "Administrador académico",
      rol: activeRole,
      accion,
      entidad: "standard",
      entidadId: codigo,
    });
  }

  // Header edit
  function startHeaderEdit() {
    setHeaderDraft({ titulo: element!.titulo, descripcion: element!.descripcion });
    setEditingHeader(true);
  }
  function saveHeader() {
    if (!headerDraft.titulo.trim()) return;
    persistElement({ ...element!, titulo: headerDraft.titulo.trim(), descripcion: headerDraft.descripcion.trim() }, "editar");
    setEditingHeader(false);
  }

  function deleteElementWithConfirm() {
    if (!window.confirm(`¿Eliminar el elemento ${element!.codigo}? Esta acción no se puede deshacer.`)) return;
    removeElement(codigo, element!.id);
    const remaining = elems.filter((e) => e.id !== element!.id);
    const allCriterios = remaining.reduce((a, e) => a + e.criterios.length, 0);
    updateStandard(codigo, {
      elementos: remaining.length,
      criterios: allCriterios,
      ultimaActualizacion: today(),
    });
    logEvent({
      actor: "Administrador académico",
      rol: activeRole,
      accion: "eliminar-elemento",
      entidad: "standard",
      entidadId: codigo,
    });
    setLocation(`/admin/estandares/${codigo}`);
  }

  // Criterio CRUD
  function startEditCriterio(c: Criterio) {
    setEditingRow(c.id);
    setCriterioDraft({ descripcion: c.descripcion, tipo: c.tipo, ponderacion: c.ponderacion });
  }
  function saveCriterio(orig: Criterio) {
    if (!criterioDraft.descripcion || !String(criterioDraft.descripcion).trim()) return;
    const next: Criterio = {
      ...orig,
      descripcion: String(criterioDraft.descripcion).trim(),
      tipo: (criterioDraft.tipo ?? orig.tipo) as CriterioTipo,
      ponderacion: Number(criterioDraft.ponderacion ?? orig.ponderacion) || 0,
    };
    persistElement({ ...element!, criterios: element!.criterios.map((c) => (c.id === orig.id ? next : c)) }, "editar-criterio");
    setEditingRow(null);
    setCriterioDraft({});
  }
  function deleteCriterio(c: Criterio) {
    if (!window.confirm(`¿Eliminar el criterio ${c.codigo}?`)) return;
    persistElement({ ...element!, criterios: element!.criterios.filter((x) => x.id !== c.id) }, "eliminar-criterio");
  }
  function reorderSection(tipo: CriterioTipo, nextSection: Criterio[]) {
    const queue = [...nextSection];
    const merged = element!.criterios.map((c) => (c.tipo === tipo ? (queue.shift() ?? c) : c));
    persistElement({ ...element!, criterios: merged }, "reordenar-criterio");
  }
  function addCriterio(tipo: CriterioTipo) {
    const desc = String(newCriterio.descripcion ?? "").trim();
    if (!desc) return;
    const nextNum = element!.criterios.length + 1;
    const item: Criterio = {
      id: uid("c"),
      codigo: `C${nextNum}`,
      descripcion: desc,
      tipo,
      ponderacion: Number(newCriterio.ponderacion ?? 5) || 5,
    };
    persistElement({ ...element!, criterios: [...element!.criterios, item] }, "agregar-criterio");
    setAdding(null);
    setNewCriterio({});
  }

  // Evidencia CRUD
  function startEditEvidencia(ev: Evidencia) {
    setEditingRow(ev.id);
    setEvidenciaDraft({ nombre: ev.nombre, tipo: ev.tipo, obligatoria: ev.obligatoria });
  }
  function saveEvidencia(orig: Evidencia) {
    if (!evidenciaDraft.nombre || !String(evidenciaDraft.nombre).trim()) return;
    const next: Evidencia = {
      ...orig,
      nombre: String(evidenciaDraft.nombre).trim(),
      tipo: (evidenciaDraft.tipo ?? orig.tipo) as EvidenciaTipo,
      obligatoria: Boolean(evidenciaDraft.obligatoria ?? orig.obligatoria),
    };
    persistElement({ ...element!, evidencias: element!.evidencias.map((e) => (e.id === orig.id ? next : e)) }, "editar-evidencia");
    setEditingRow(null);
    setEvidenciaDraft({});
  }
  function deleteEvidencia(ev: Evidencia) {
    if (!window.confirm(`¿Eliminar la evidencia "${ev.nombre}"?`)) return;
    persistElement({ ...element!, evidencias: element!.evidencias.filter((x) => x.id !== ev.id) }, "eliminar-evidencia");
  }
  function reorderEvidencias(next: Evidencia[]) {
    persistElement({ ...element!, evidencias: next }, "reordenar-evidencia");
  }
  function addEvidencia() {
    const nombre = String(newEvidencia.nombre ?? "").trim();
    if (!nombre) return;
    const item: Evidencia = {
      id: uid("ev"),
      nombre,
      tipo: (newEvidencia.tipo ?? "documento") as EvidenciaTipo,
      obligatoria: Boolean(newEvidencia.obligatoria ?? true),
    };
    persistElement({ ...element!, evidencias: [...element!.evidencias, item] }, "agregar-evidencia");
    setAdding(null);
    setNewEvidencia({});
  }

  // AI suggestions: criterios faltantes
  function openAiSuggest() {
    const suggested = suggestElements(codigo);
    const matched = suggested.find((s) => s.codigo === element!.codigo) ?? suggested[0];
    const existingDesc = new Set(element!.criterios.map((c) => c.descripcion.toLowerCase().slice(0, 40)));
    const nuevos = matched.criterios.filter((c) => !existingDesc.has(c.descripcion.toLowerCase().slice(0, 40)));
    const renumerados: Criterio[] = nuevos.map((c, idx) => ({
      ...c,
      id: uid("c"),
      codigo: `C${element!.criterios.length + idx + 1}`,
    }));
    setAiPreview(renumerados);
    setAiSel(Object.fromEntries(renumerados.map((c) => [c.id, true])));
  }
  function applyAi() {
    if (!aiPreview) return;
    const sel = aiPreview.filter((c) => aiSel[c.id]);
    if (sel.length === 0) {
      setAiPreview(null);
      return;
    }
    persistElement({ ...element!, criterios: [...element!.criterios, ...sel] }, "sugerencia-ia-criterios");
    setAiPreview(null);
    setAiSel({});
  }

  // Group criterios by tipo preservando orden
  const grouped: Record<CriterioTipo, Criterio[]> = {
    desempeño: [],
    producto: [],
    conocimiento: [],
    actitud: [],
    emergencia: [],
  };
  for (const c of element.criterios) grouped[c.tipo].push(c);

  const totalPonderacion = element.criterios.reduce((a, c) => a + c.ponderacion, 0);

  return (
    <AppLayout breadcrumb={["Estándares", codigo, element.codigo, "Editor"]}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <Badge tone="teal">{element.codigo}</Badge>
            <span style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600 }}>{codigo}</span>
            <span style={{ fontSize: 11, color: "var(--cc-gray-400)" }}>· {element.criterios.length} criterios · {element.evidencias.length} evidencias</span>
          </div>
          {editingHeader ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 780 }}>
              <input
                value={headerDraft.titulo}
                onChange={(e) => setHeaderDraft({ ...headerDraft, titulo: e.target.value })}
                data-testid="edit-element-titulo"
                style={inputStyle({ fontSize: 18, fontWeight: 700 })}
                placeholder="Título del elemento"
              />
              <textarea
                value={headerDraft.descripcion}
                onChange={(e) => setHeaderDraft({ ...headerDraft, descripcion: e.target.value })}
                data-testid="edit-element-desc"
                rows={3}
                style={{ ...inputStyle({ fontSize: 13 }), fontFamily: "inherit", resize: "vertical" }}
                placeholder="Descripción del elemento"
              />
              <div style={{ display: "flex", gap: 8 }}>
                <PrimaryButton size="sm" variant="dark" onClick={saveHeader} testId="button-save-element"><Save size={13} /> Guardar</PrimaryButton>
                <PrimaryButton size="sm" variant="ghost" onClick={() => setEditingHeader(false)} testId="button-cancel-element"><X size={13} /> Cancelar</PrimaryButton>
              </div>
            </div>
          ) : (
            <>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)", maxWidth: 780 }}>{element.titulo}</h1>
              <div style={{ fontSize: 13, color: "var(--cc-gray-600)", marginTop: 4, lineHeight: 1.5, maxWidth: 780 }}>{element.descripcion}</div>
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {!editingHeader && (
            <PrimaryButton variant="outline" onClick={startHeaderEdit} testId="button-edit-element"><Pencil size={13} /> Editar</PrimaryButton>
          )}
          <PrimaryButton variant="outline" onClick={deleteElementWithConfirm} testId="button-delete-element"><Trash2 size={13} /> Eliminar</PrimaryButton>
          <Link href={`/admin/estandares/${codigo}/elementos`} data-testid="link-next-instruments">
            <PrimaryButton variant="dark">Volver a elementos <ArrowRight size={14} /></PrimaryButton>
          </Link>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {tipoOrder.map((tipo) => {
            const items = grouped[tipo];
            const open = openSection[tipo];
            return (
              <Card key={tipo} padding={0} testId={`section-${tipo}`}>
                <button
                  onClick={() => setOpenSection({ ...openSection, [tipo]: !open })}
                  data-testid={`toggle-${tipo}`}
                  style={collapseHeader}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <span style={{ fontWeight: 700, color: "var(--cc-navy)", fontSize: 14 }}>{tipoLabel[tipo]}</span>
                    <Badge tone={tipoTone[tipo]}>{items.length}</Badge>
                  </div>
                  <span style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>Pond. {items.reduce((a, c) => a + c.ponderacion, 0)}%</span>
                </button>
                {open && (
                  <div style={{ padding: 12, borderTop: "1px solid var(--cc-gray-100)" }}>
                    {items.length === 0 && adding !== tipo && (
                      <div style={{ fontSize: 12, color: "var(--cc-gray-500)", padding: "6px 4px 10px" }}>Sin criterios en esta sección.</div>
                    )}
                    <SortableList
                      contextId={`crit-${tipo}`}
                      items={items}
                      idFor={(c) => c.id}
                      onReorder={(next) => reorderSection(tipo, next)}
                      renderItem={(c, _idx, drag) => (
                        <div data-testid={`row-criterio-${c.codigo}`} style={rowStyle}>
                          <DragHandle drag={drag} />
                          <div style={{ fontWeight: 700, color: "var(--cc-navy)", width: 36, fontSize: 12 }}>{c.codigo}</div>
                          {editingRow === c.id ? (
                            <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 130px 70px auto", gap: 6, alignItems: "center" }}>
                              <input
                                value={String(criterioDraft.descripcion ?? "")}
                                onChange={(e) => setCriterioDraft({ ...criterioDraft, descripcion: e.target.value })}
                                data-testid={`input-desc-${c.codigo}`}
                                style={inputStyle({ fontSize: 12 })}
                              />
                              <select
                                value={String(criterioDraft.tipo ?? c.tipo)}
                                onChange={(e) => setCriterioDraft({ ...criterioDraft, tipo: e.target.value as CriterioTipo })}
                                data-testid={`select-tipo-${c.codigo}`}
                                style={inputStyle({ fontSize: 12 })}
                              >
                                {tipoOrder.map((t) => <option key={t} value={t}>{t}</option>)}
                              </select>
                              <input
                                type="number"
                                value={Number(criterioDraft.ponderacion ?? c.ponderacion)}
                                onChange={(e) => setCriterioDraft({ ...criterioDraft, ponderacion: Number(e.target.value) })}
                                data-testid={`input-pond-${c.codigo}`}
                                style={inputStyle({ fontSize: 12 })}
                              />
                              <div style={{ display: "flex", gap: 4 }}>
                                <button onClick={() => saveCriterio(c)} data-testid={`save-${c.codigo}`} style={miniBtn("dark")}><Check size={12} /></button>
                                <button onClick={() => { setEditingRow(null); setCriterioDraft({}); }} style={miniBtn("ghost")}><X size={12} /></button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div style={{ flex: 1, fontSize: 12, color: "var(--cc-gray-700)", lineHeight: 1.5 }}>
                                {c.descripcion}
                                {(coverage.get(c.id) ?? []).length === 0 && (
                                  <span style={{ marginLeft: 8, display: "inline-block" }}>
                                    <Badge tone="red">Sin instrumento</Badge>
                                  </span>
                                )}
                                {c.subitems && c.subitems.length > 0 && (
                                  <ul style={{ margin: "6px 0 0 0", paddingLeft: 18, color: "var(--cc-gray-600)", fontSize: 11, lineHeight: 1.45 }}>
                                    {c.subitems.map((s, i) => (
                                      <li key={i}>{s}</li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <div style={{ width: 60, textAlign: "right", fontSize: 12, color: "var(--cc-gray-700)", fontWeight: 600 }}>{c.ponderacion}%</div>
                              <div style={{ display: "flex", gap: 4 }}>
                                <button onClick={() => startEditCriterio(c)} data-testid={`edit-${c.codigo}`} style={miniBtn("ghost")}><Pencil size={12} /></button>
                                <button onClick={() => deleteCriterio(c)} data-testid={`del-${c.codigo}`} style={miniBtn("ghost")}><Trash2 size={12} /></button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    />
                    {adding === tipo ? (
                      <div style={{ ...rowStyle, alignItems: "flex-start" }}>
                        <div style={{ width: 30 }} />
                        <div style={{ width: 36, fontSize: 11, color: "var(--cc-gray-500)" }}>nuevo</div>
                        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 70px auto", gap: 6 }}>
                          <input autoFocus value={String(newCriterio.descripcion ?? "")} onChange={(e) => setNewCriterio({ ...newCriterio, descripcion: e.target.value })} placeholder="Descripción del criterio" data-testid="new-criterio-desc" style={inputStyle({ fontSize: 12 })} />
                          <input type="number" value={Number(newCriterio.ponderacion ?? 5)} onChange={(e) => setNewCriterio({ ...newCriterio, ponderacion: Number(e.target.value) })} placeholder="%" data-testid="new-criterio-pond" style={inputStyle({ fontSize: 12 })} />
                          <div style={{ display: "flex", gap: 4 }}>
                            <button onClick={() => addCriterio(tipo)} data-testid={`confirm-add-${tipo}`} style={miniBtn("dark")}><Check size={12} /></button>
                            <button onClick={() => { setAdding(null); setNewCriterio({}); }} style={miniBtn("ghost")}><X size={12} /></button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setAdding(tipo); setNewCriterio({ ponderacion: 5 }); }} data-testid={`add-${tipo}`} style={addBtn}>
                        <Plus size={12} /> Agregar criterio
                      </button>
                    )}
                  </div>
                )}
              </Card>
            );
          })}

          <Card padding={0} testId="section-evidencias">
            <button onClick={() => setOpenSection({ ...openSection, evidencias: !openSection.evidencias })} style={collapseHeader} data-testid="toggle-evidencias">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {openSection.evidencias ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span style={{ fontWeight: 700, color: "var(--cc-navy)", fontSize: 14 }}>Instrumentos para recolectar evidencias</span>
                <Badge tone="navy">{element.evidencias.length}</Badge>
              </div>
              <span style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{element.evidencias.filter((e) => e.obligatoria).length} obligatorias</span>
            </button>
            {openSection.evidencias && (
              <div style={{ padding: 12, borderTop: "1px solid var(--cc-gray-100)" }}>
                <SortableList
                  contextId="evidencias"
                  items={element.evidencias}
                  idFor={(ev) => ev.id}
                  onReorder={reorderEvidencias}
                  renderItem={(ev, _idx, drag) => {
                    const Icon = evidenceIcons[ev.tipo];
                    return (
                      <div data-testid={`row-evidencia-${ev.id}`} style={rowStyle}>
                        <DragHandle drag={drag} />
                        <Icon size={14} color="var(--cc-teal)" />
                        {editingRow === ev.id ? (
                          <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 130px 110px auto", gap: 6 }}>
                            <input value={String(evidenciaDraft.nombre ?? "")} onChange={(e) => setEvidenciaDraft({ ...evidenciaDraft, nombre: e.target.value })} style={inputStyle({ fontSize: 12 })} />
                            <select value={String(evidenciaDraft.tipo ?? ev.tipo)} onChange={(e) => setEvidenciaDraft({ ...evidenciaDraft, tipo: e.target.value as EvidenciaTipo })} style={inputStyle({ fontSize: 12 })}>
                              {(["video", "foto", "documento", "audio"] as EvidenciaTipo[]).map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                              <input type="checkbox" checked={Boolean(evidenciaDraft.obligatoria ?? ev.obligatoria)} onChange={(e) => setEvidenciaDraft({ ...evidenciaDraft, obligatoria: e.target.checked })} /> Obligatoria
                            </label>
                            <div style={{ display: "flex", gap: 4 }}>
                              <button onClick={() => saveEvidencia(ev)} style={miniBtn("dark")}><Check size={12} /></button>
                              <button onClick={() => { setEditingRow(null); setEvidenciaDraft({}); }} style={miniBtn("ghost")}><X size={12} /></button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div style={{ flex: 1, fontSize: 12, color: "var(--cc-navy)", fontWeight: 600 }}>{ev.nombre}</div>
                            <div style={{ width: 80, fontSize: 11, color: "var(--cc-gray-500)", textTransform: "capitalize" }}>{ev.tipo}</div>
                            {ev.obligatoria ? <Badge tone="red">Obligatoria</Badge> : <Badge>Opcional</Badge>}
                            <div style={{ display: "flex", gap: 4 }}>
                              <button onClick={() => startEditEvidencia(ev)} data-testid={`edit-ev-${ev.id}`} style={miniBtn("ghost")}><Pencil size={12} /></button>
                              <button onClick={() => deleteEvidencia(ev)} data-testid={`del-ev-${ev.id}`} style={miniBtn("ghost")}><Trash2 size={12} /></button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  }}
                />
                {adding === "evidencia" ? (
                  <div style={{ ...rowStyle, alignItems: "flex-start" }}>
                    <div style={{ width: 30 }} />
                    <FileText size={14} color="var(--cc-gray-400)" />
                    <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 130px 110px auto", gap: 6 }}>
                      <input autoFocus placeholder="Nombre de la evidencia" value={String(newEvidencia.nombre ?? "")} onChange={(e) => setNewEvidencia({ ...newEvidencia, nombre: e.target.value })} data-testid="new-evidencia-nombre" style={inputStyle({ fontSize: 12 })} />
                      <select value={String(newEvidencia.tipo ?? "documento")} onChange={(e) => setNewEvidencia({ ...newEvidencia, tipo: e.target.value as EvidenciaTipo })} style={inputStyle({ fontSize: 12 })}>
                        {(["video", "foto", "documento", "audio"] as EvidenciaTipo[]).map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                        <input type="checkbox" checked={Boolean(newEvidencia.obligatoria ?? true)} onChange={(e) => setNewEvidencia({ ...newEvidencia, obligatoria: e.target.checked })} /> Obligatoria
                      </label>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={addEvidencia} data-testid="confirm-add-evidencia" style={miniBtn("dark")}><Check size={12} /></button>
                        <button onClick={() => { setAdding(null); setNewEvidencia({}); }} style={miniBtn("ghost")}><X size={12} /></button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => { setAdding("evidencia"); setNewEvidencia({ tipo: "documento", obligatoria: true }); }} data-testid="add-evidencia" style={addBtn}>
                    <Plus size={12} /> Agregar evidencia
                  </button>
                )}
              </div>
            )}
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionTitle>Resumen del elemento</SectionTitle>
            <Stat label="Criterios" value={String(element.criterios.length)} />
            <Stat label="Ponderación total" value={`${totalPonderacion}%`} />
            <Stat label="Evidencias" value={String(element.evidencias.length)} />
            <Stat label="Instrumentos vinculados" value={String(instruments.filter((i) => i.elementoId === element.id).length)} />
            {totalPonderacion !== 100 && (
              <div style={{ marginTop: 8, fontSize: 11, color: "var(--cc-red)" }}>⚠ La ponderación no suma 100%.</div>
            )}
            {element.criterios.some((c) => (coverage.get(c.id) ?? []).length === 0) && (
              <div style={{ marginTop: 6, fontSize: 11, color: "var(--cc-red)" }}>⚠ Hay criterios sin instrumento que los evalúe.</div>
            )}
          </Card>

          <Card style={{ background: "var(--cc-teal-bg)", border: "1px solid #a5f3fc" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Sparkles size={14} color="var(--cc-teal)" />
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Sugerir con IA</span>
            </div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-700)", lineHeight: 1.5, marginBottom: 10 }}>
              La IA propondrá criterios complementarios coherentes con el estándar {codigo}.
            </div>
            {!aiPreview ? (
              <PrimaryButton size="sm" variant="dark" onClick={openAiSuggest} testId="button-ai-suggest"><Sparkles size={12} /> Sugerir criterios</PrimaryButton>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {aiPreview.length === 0 && (
                  <div style={{ fontSize: 12, color: "var(--cc-gray-600)" }}>No hay sugerencias nuevas: el elemento ya cubre lo recomendado por la IA.</div>
                )}
                {aiPreview.map((c) => (
                  <label key={c.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "white", borderRadius: 8, padding: 8, fontSize: 12 }} data-testid={`ai-suggest-${c.id}`}>
                    <input type="checkbox" checked={Boolean(aiSel[c.id])} onChange={(e) => setAiSel({ ...aiSel, [c.id]: e.target.checked })} />
                    <div style={{ flex: 1 }}>
                      <div style={{ color: "var(--cc-navy)" }}>{c.descripcion}</div>
                      <div style={{ fontSize: 10, color: "var(--cc-gray-500)", marginTop: 2, textTransform: "capitalize" }}>{c.tipo} · {c.ponderacion}%</div>
                    </div>
                  </label>
                ))}
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <PrimaryButton size="sm" variant="dark" onClick={applyAi} testId="button-ai-apply"><Check size={12} /> Aceptar selección</PrimaryButton>
                  <PrimaryButton size="sm" variant="ghost" onClick={() => { setAiPreview(null); setAiSel({}); }}><X size={12} /> Descartar</PrimaryButton>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}

const collapseHeader: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  textAlign: "left",
};
const rowStyle: React.CSSProperties = {
  display: "flex",
  gap: 10,
  alignItems: "center",
  padding: "8px 6px",
  borderTop: "1px solid var(--cc-gray-100)",
};
const addBtn: React.CSSProperties = {
  marginTop: 8,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "transparent",
  border: "1px dashed var(--cc-gray-300)",
  color: "var(--cc-teal)",
  padding: "6px 10px",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

function inputStyle(extra: React.CSSProperties = {}): React.CSSProperties {
  return {
    padding: "6px 8px",
    border: "1px solid var(--cc-gray-200)",
    borderRadius: 6,
    background: "white",
    color: "var(--cc-gray-800)",
    width: "100%",
    ...extra,
  };
}
function miniBtn(kind: "dark" | "ghost"): React.CSSProperties {
  return {
    width: 26,
    height: 26,
    borderRadius: 6,
    border: kind === "dark" ? "none" : "1px solid var(--cc-gray-200)",
    background: kind === "dark" ? "var(--cc-navy)" : "white",
    color: kind === "dark" ? "white" : "var(--cc-gray-700)",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  };
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--cc-gray-100)" }}>
      <span style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{value}</span>
    </div>
  );
}
